import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from 'app/core/triggerbee/triggerbee-api.enum';
import {
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterAppSetting,
  AudienceFilterType,
} from 'app/audience/components/audience.model';
import { InterestProfile } from '../../account-settings/models/interest-profiles.model';
import {
  IntegrationListKeyValuePair,
  IntegrationSettings,
  IntegrationType,
  ProductRecommendationsIntegration,
} from '../../automations/integrations-models';
import IconsHelper from '../helpers/icons-helper';

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private readonly logger: Logger;

  public activeIntegrationsBS = new BehaviorSubject<IntegrationSettings[]>(undefined);
  private productRecommendationsIntegrationsBS = new BehaviorSubject<ProductRecommendationsIntegration[]>(undefined);

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('IntegrationsService');
  }

  private getResourceBase() {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/integrations`;
  }

  public getSettings(filter: AudienceFilter, listId?): Observable<AudienceFilterAppSetting[]> {
    let url = this.getResourceBase() + '/';
    if (!listId) {
      url += this.getAppName(filter.app);
    } else {
      url =
        this.http.getBaseUrl(TriggerbeeApi.Gateway) +
        `/integrations/tags/${this.getAppName(filter.app)}/${encodeURIComponent(listId)}`;
      return this.http.get(url);
    }
    switch (filter.type) {
      case AudienceFilterType.AppList:
      case AudienceFilterType.AppTags:
        url += '/lists';
        break;
      case AudienceFilterType.AppSegment:
        url += '/segments';
        break;
      case AudienceFilterType.AppLabels:
        url += '/labels';
        break;
      default:
        throw new Error(`Type '${filter.type}' is not supported`);
    }
    return this.http.get(url);
  }

  public getInterestProfiles(): Observable<Array<InterestProfile>> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/interest-profiles';
    return this.http.get(url);
  }

  public getListsForApp(appName: string): Observable<Array<{ id: string; name: string }>> {
    const url = `${this.getResourceBase()}/${this.getAppName((<any>AudienceFilterApp)[appName])}/lists`;
    return this.http.get(url);
  }

  public getListForApp(appName: string): Observable<Array<{ id: string; name: string }>> {
    const url = `${this.getResourceBase()}/${appName}/lists`;
    return this.http.get(url);
  }

  public getDataForActionProperties(url: string): any {
    return this.http.get(`${this.getResourceBase()}${url}`);
  }

  public getDataForActionPropertyTrigerbee(url: string): Observable<any> {
    return this.http.get(this.http.getBaseUrl(TriggerbeeApi.Gateway) + `${url}`);
  }

  public getActiveApps(): Observable<string[]> {
    const url = `${this.getResourceBase()}`;
    return this.http.get(url).pipe(map((apps) => apps.map((app) => app.type)));
  }

  public getQueryStrings(): Observable<{ [key in IntegrationType]: string }> {
    const url = `${this.getResourceBase()}/query-strings?hasUpdateSubscriptions=true`;
    return this.http.get(url);
  }

  public getAppName(filterApp: AudienceFilterApp) {
    // TODO: MOVE TO HELPER..
    // Convert to actual app-name in legacy (it's the name of extension-class without word "Extension")
    switch (filterApp) {
      case AudienceFilterApp.Rule:
        return 'RuleMailer'; // This class name is actually RuleMailerExtension for example
      case AudienceFilterApp.Mailchimp:
        return 'Mailchimp';
      case AudienceFilterApp.Voyado:
        return 'Voyado';
      case AudienceFilterApp.Klaviyo:
        return 'Klaviyo';
      case AudienceFilterApp.Emarsys:
        return 'Emarsys';
      case AudienceFilterApp.Custobar:
        return 'Custobar';
      case AudienceFilterApp.MailerLite:
        return 'MailerLite';
      case AudienceFilterApp.Shopify:
        return 'Shopify';
      case AudienceFilterApp.Brevo:
        return 'Brevo';
      case AudienceFilterApp.Dotdigital:
        return 'Dotdigital';
      case AudienceFilterApp.Omnisend:
        return 'Omnisend';
      case AudienceFilterApp.CleverReach:
        return 'CleverReach';
      case AudienceFilterApp.Shopware:
        return 'Shopware';
      case AudienceFilterApp.Custom:
        return 'Custom';
      default:
        throw new Error(`App '${filterApp}' is not supported`);
    }
  }

  public static getIconPath(integration: IntegrationType, withoutTextIfExists?: boolean) {
    switch (integration) {
      case IntegrationType.Pipedrive:
      case IntegrationType.RuleMailer:
      case IntegrationType.Rule:
      case IntegrationType.MailChimp:
      case IntegrationType.MailerLite:
      case IntegrationType.Apsis:
      case IntegrationType.ApsisOne:
      case IntegrationType.Zapier:
      case IntegrationType.Voyado:
      case IntegrationType.Klaviyo:
      case IntegrationType.Emarsys:
      case IntegrationType.Custobar:
      case IntegrationType.MailerSend:
      case IntegrationType.Brevo:
      case IntegrationType.Dotdigital:
      case IntegrationType.Omnisend:
      case IntegrationType.CleverReach:
      case IntegrationType.Shopware:
      case IntegrationType.Custom:
        return IconsHelper.getLogoPath(integration.toString(), withoutTextIfExists);
      default:
        throw new Error(`Integration icon path not implemented: ${integration}`);
    }
  }

  public getTags(filter: AudienceFilter, listId: string): any {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) +
      `/integrations/tags/${this.getAppName(filter.app)}/${encodeURIComponent(listId)}`;
    return this.http.get(url);
  }

  public getActiveIntegrations(): Observable<IntegrationSettings[]> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/integrations';
    return this.http.get(url);
  }

  public loadActiveIntegrations() {
    this.getActiveIntegrations().subscribe((integrations) => {
      this.activeIntegrationsBS.next(integrations);
    });
  }

  public loadProductRecommendationsIntegrations() {
    this.getProductRecommendationsIntegrations().subscribe((integrations) => {
      this.productRecommendationsIntegrationsBS.next(integrations);
    });
  }

  public get productRecommendationsIntegrations() {
    return this.productRecommendationsIntegrationsBS.value;
  }

  private getProductRecommendationsIntegrations(): Observable<Array<ProductRecommendationsIntegration>> {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/integrations/product-recommendations/active-integrations';
    return this.http.get(url);
  }

  public getProductRecommendationsProperties(integration: IntegrationType): Observable<Array<string>> {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/integrations/product-recommendations/${integration}/merge-vars`;
    return this.http.get(url);
  }

  public getIntegrationDetails(integrationType: IntegrationType): Observable<IntegrationSettings> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/integrations/' + integrationType;
    return this.http.get(url);
  }

  public authenticateIntegration(integrationType: IntegrationType, authenticationInput: any): Observable<boolean> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/integrations/settings/${integrationType}/authenticate`;
    authenticationInput['$type'] =
      `Triggerbee.Integrations.Client.Settings.Models.${integrationType}SettingsRequest, Triggerbee.Integrations.Client`;
    return this.http.post(url, authenticationInput);
  }

  public updateIntegrationConfig(
    integrationType: IntegrationType,
    configurationInput: any,
  ): Observable<IntegrationSettings> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/integrations/settings/${integrationType}`;
    configurationInput['$type'] =
      `Triggerbee.Integrations.Client.Settings.Models.UpdateSettings${integrationType}Request, Triggerbee.Integrations.Client`;
    return this.http.put(url, configurationInput);
  }

  public loadOptionsForProperty(
    optionsUrl: string,
    queryParamsToUse?: any,
  ): Observable<Array<IntegrationListKeyValuePair>> {
    if (queryParamsToUse) {
      Object.keys(queryParamsToUse).forEach((key) => {
        optionsUrl = optionsUrl.replace(`{${key}}`, queryParamsToUse[key]);
      });
    }
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + optionsUrl;
    return this.http.get(url);
  }

  public getOAuthAuthorizeUrl(integration: IntegrationType): Observable<{ authorizeUrl: string }> {
    const url = `${this.getResourceBase()}/settings/${integration}/oauth/authorize-url`;
    return this.http.get(url);
  }

  public deleteIntegrationConfig(integrationType: IntegrationType): Observable<void> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/integrations/settings/${integrationType}`;
    return this.http.delete(url);
  }

  public static getDataSourceTranslationKey(integration: IntegrationType) {
    switch (integration) {
      case IntegrationType.Rule:
      case IntegrationType.RuleMailer:
        return 'campaign.widget.sidebar.edit.dataSource.items.rule';
      case IntegrationType.MailerLite:
        return 'campaign.widget.sidebar.edit.dataSource.items.mailerlite';
      case IntegrationType.Custobar:
        return 'campaign.widget.sidebar.edit.dataSource.items.custobar';
      case IntegrationType.Klaviyo:
        return 'campaign.widget.sidebar.edit.dataSource.items.klaviyo';
      case IntegrationType.Brevo:
        return 'campaign.widget.sidebar.edit.dataSource.items.brevo';
      default:
        throw new Error(`Integration '${integration}' translation not implemented`);
    }
  }

  public static convertOldIntegrationTypes(integration: IntegrationType) {
    // Convert from obsolete type (it's still used)
    if (integration == IntegrationType.RuleMailer) {
      return IntegrationType.Rule;
    }
    return integration;
  }
}
